import {createReducer, on} from '@ngrx/store';
import {
    MarketplaceFilteredProducts,
    MarketplaceFilters,
    SearchProductSortType,
    MarketplaceState} from './marketplace.state';
import {
    fetchProducts,
    fetchProductsFailure,
    fetchProductsSuccess,
    setMarketplaceFilters,
    setMarketplaceSort,
    toggleMarketplaceShowFilters
} from './marketplace.actions';
import {Product} from "../../models/product.model";
import {
    buildMarketplaceScreenMockAvailableFilters,
    buildMarketplaceScreenMockFilteredProducts,
    isMarketplaceScreenMockEnabled
} from "./marketplace.screen.mock";

const initialState: MarketplaceState = {
    data: null,
    filteredProducts: {
        featured: [],
        bundle: [],
        subscription: [],
        oneShotPurchase: [],
        categories: {},
    },
    availableFilters: {
        categories: [],
        goals: [],
        families: [],
        subscriptionRecurringPeriod: [],
        maxPrice: [0, 0],
    },
    filters: {
        categories: [],
        goals: [],
        families: [],
        subscriptionRecurringPeriod: [],
        maxPrice: [0, 0],
    },
    sort: null,
    error: null,
    isLoading: false,
    showFilters: false,
};

export const marketplaceReducer = createReducer(
    initialState,
    on(fetchProducts, (state) => ({
        ...state,
        isLoading: true,
        error: null,
    })),
    on(fetchProductsSuccess, (state, {data}) => ({
        ...state,
        data: data,
        filteredProducts: isMarketplaceScreenMockEnabled()
            ? buildMarketplaceScreenMockFilteredProducts(state.filters, state.sort)
            : buildFilteredProducts(data, state.filters, state.sort),
        availableFilters: isMarketplaceScreenMockEnabled()
            ? buildMarketplaceScreenMockAvailableFilters()
            : buildAvailableFilters(data),
        error: null,
        isLoading: false,
    })),
    on(fetchProductsFailure, (state, {error}) => ({
        ...state,
        error: error,
        isLoading: false,
    })),
    on(setMarketplaceFilters, (state, {filters}) => ({
        ...state,
        filteredProducts: isMarketplaceScreenMockEnabled()
            ? buildMarketplaceScreenMockFilteredProducts({
                ...state.filters,
                ...filters,
            }, state.sort)
            : buildFilteredProducts(state.data ?? [], {
                ...state.filters,
                ...filters,
            }, state.sort),
        filters: {
            ...state.filters,
            ...filters,
        },
    })),
    on(toggleMarketplaceShowFilters, (state) => ({
        ...state,
        showFilters: !state.showFilters,
    })),
    on(setMarketplaceSort, (state, {sort}) => ({
        ...state,
        filteredProducts: isMarketplaceScreenMockEnabled()
            ? buildMarketplaceScreenMockFilteredProducts(state.filters, sort)
            : buildFilteredProducts(state.data ?? [], state.filters, sort),
        sort,
    })),
);

const buildAvailableFilters = (products: Product[]): MarketplaceFilters => {
    products = [...products];

    const filters: MarketplaceFilters = {
        categories: [],
        goals: [],
        families: [],
        subscriptionRecurringPeriod: [],
        maxPrice: [0, 0],
    };

    for (const product of products) {
        if (product.categories) {
            filters.categories.push(...product.categories);
            if (product.categories.length > 1) {
                filters.categories.push('bundle');
            }
            filters.categories = [...new Set(filters.categories)];
        }
        if (product.goals) {
            filters.goals.push(...product.goals);
            filters.goals = [...new Set(filters.goals)];
        }
        if (product.family) {
            filters.families.push(product.family);
            filters.families = [...new Set(filters.families)];
        }
        if (product.family === 'Subscription' && product.subscriptionRecurringPeriod) {
            filters.subscriptionRecurringPeriod.push((product.subscriptionRecurringInterval ?? '') + (product.subscriptionRecurringPeriod ?? ''));
            filters.subscriptionRecurringPeriod = [...new Set(filters.subscriptionRecurringPeriod)];
        }
        if (product.price > 0) {
            filters.maxPrice[1] = Math.max(filters.maxPrice[1], product.price);
        }
    }

    return filters;
}

const buildFilteredProducts = (products: Product[], filters: MarketplaceFilters, sort?: SearchProductSortType | null): MarketplaceFilteredProducts => {
    products = [...products];

    const filteredProducts: MarketplaceFilteredProducts = {
        featured: [],
        bundle: [],
        subscription: [],
        oneShotPurchase: [],
        categories: {},
    };

    const maxPrice = products.reduce((currentMaxPrice, product) => Math.max(currentMaxPrice, product.price), 0);

    const filtersActive = filters.families.length > 0 || filters.categories.length > 0 || filters.goals.length > 0 ||
        filters.subscriptionRecurringPeriod.length > 0 || filters.maxPrice[0] > 0 || (filters.maxPrice[1] > 0 && filters.maxPrice[1] !== maxPrice);

    products = sortProducts(products, sort);

    for (const product of products) {
        if (filters.categories.length > 0 && !(product.categories ?? []).some(category => filters.categories.includes(category))) {
            if (!filters.categories.includes('bundle') ||
                (filters.categories.includes('bundle') && !((product.categories ?? []).length > 1))) {
                continue;
            }
        }
        if (filters.goals.length > 0 && !(product.goals ?? []).some(objective => filters.goals.includes(objective))) {
            continue;
        }
        if (filters.families.length > 0 && !filters.families.includes(product.family)) {
            continue;
        }
        if (filters.subscriptionRecurringPeriod.length > 0 &&
            (product.family !== 'Subscription' ||
                !filters.subscriptionRecurringPeriod.includes((product.subscriptionRecurringInterval ?? '') + (product.subscriptionRecurringPeriod ?? '')))) {
            continue;
        }
        if (filters.maxPrice[0] > 0 && product.price < filters.maxPrice[0]) {
            continue;
        }
        if (filters.maxPrice[1] > 0 && product.price > filters.maxPrice[1]) {
            continue;
        }

        if (!filtersActive) {
            if (product.isFeaturedProduct || product.originalPrice) {
                filteredProducts.featured.push(product);
            }
            if (product.categories.length > 1) {
                filteredProducts.bundle.push(product);
            }
            if (product.family === 'Subscription') {
                filteredProducts.subscription.push(product);
            }
            if (product.family === 'One shot purchase' && product.categories.length <= 1) {
                filteredProducts.oneShotPurchase.push(product);
            }
        }

        for (const category of (product.categories ?? [])) {
            if (!filteredProducts.categories[category]) {
                filteredProducts.categories[category] = [];
            }
            filteredProducts.categories[category].push(product);
        }
    }

    return filteredProducts;
}

const sortProducts = (products: Product[], sort?: SearchProductSortType | null): Product[] => {
    return products
        .map((product, index) => ({product, index}))
        .sort((left, right) => {
            switch (sort) {
                case "price:desc":
                    return right.product.price - left.product.price;
                case "price:asc":
                    return left.product.price - right.product.price;
                default:
                    return compareProductsBySortOrder(left.product, right.product, left.index, right.index);
            }
        })
        .map(({product}) => product);
};

const compareProductsBySortOrder = (left: Product, right: Product, leftIndex: number, rightIndex: number): number => {
    const leftSortOrder = left.sortOrder;
    const rightSortOrder = right.sortOrder;

    if (leftSortOrder != null && rightSortOrder != null && leftSortOrder !== rightSortOrder) {
        return leftSortOrder - rightSortOrder;
    }

    if (leftSortOrder != null && rightSortOrder == null) {
        return -1;
    }

    if (leftSortOrder == null && rightSortOrder != null) {
        return 1;
    }

    return leftIndex - rightIndex;
};
